<template>
  <div class="modal" v-if="isVisible" :class="{ 'dark-mode': isDarkMode }">
    <link rel="stylesheet" :href="themeLink" id="theme-stylesheet" />
    <div class="modal-content">
      <header class="wallet-header" @click="close">
        <img
          src="../../images/back.png"
          alt="Back Button"
          class="back-button"
        />
        <h1>Wallet</h1>
      </header>
      <div class="consumption_card performance">
        <img
          src="../../images/rectangle.png"
          alt="Top Right Image"
          class="top-right-image"
        />
        <div class="wallet-info">
          <p class="wallet-balance">Your Wallet Balance</p>
          <p class="wallet-date">
            As On
            {{
              new Intl.DateTimeFormat("en-US", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
                .format(new Date())
                .replace(/\//g, "-")
            }}
          </p>
        </div>
        <div class="wallet-amount">
          <span> ₹{{ walletBalance ? walletBalance : "0" }}</span>
        </div>
      </div>

      <div  class="add-money-section">
        <p class="add-money-text">Add Money to Wallet</p>
        <input
          v-model="manualAmount"
          type="number"
          placeholder="Enter amount"
          class="add-money-input"
        />
        <div class="amount-pills">
          <button @click="setAmount(100)" class="amount-pill">+100</button>
          <button @click="setAmount(500)" class="amount-pill">+500</button>
          <button @click="setAmount(1000)" class="amount-pill">+1000</button>
        </div>
        <button v-if="!isLoading" @click="addMoney" class="add-money-button">
          Add Money
        </button>
        <button v-if="isLoading" class="add-money-button">
          Adding Money...
        </button>
      </div>

      <div class="transaction-history">
        <h2>Transaction History</h2>
        <table v-if="transactions.length > 0">
          <thead>
            <tr>
              <th width="90">Date</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Status</th> 

            </tr>
          </thead>
          <tbody>
            <tr v-for="transaction in transactions" :key="transaction.ts">
              <td class="transaction-date">
                {{
                  new Intl.DateTimeFormat("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                  })
                    .format(new Date(transaction.ts))
                    .replace(/\//g, "-")
                }}
              </td>
              <td
                class="transaction-amount"
                :style="'color:' + (transaction.amount > 0 ? 'green' : 'red')"
              >
                {{ transaction.amount }}
              </td>
              <td class="transaction-type">{{ transaction.txType }}</td>
              <td
                class="transaction-status"
                :class="{
                  success: transaction.status === 'SUCCESS',
                  failed: transaction.status === 'FAILED',
                  pending: transaction.status === 'PENDING',
                }"
              >
                {{ transaction.status }}
              </td>            </tr>
          </tbody>
        </table>
        <p v-else>No transactions found</p>
      </div>
      <button class="close-modal" @click="close">Close</button>
    </div>
    <div v-if="isLoading" class="transaction-loader-overlay">
      <div class="transaction-loader"></div>
    </div>
  </div>
</template>

<script>
import TransactionService from "@/services/transaction.service";
import UserService from "@/services/user.service";
import { useToast } from "vue-toastification";

export default {
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    walletBalance: {
      type: Number,
      default: false,
    },
    transactions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isDarkMode: false,
      manualAmount: 0,
      meta: null,
      scriptLoaded: false,
      isLoading: false,
      date: new Date(),
    };
  },
  computed: {
    themeLink() {
      return this.isDarkMode ? "/dark-theme.css" : "";
    },
  },
  async mounted() {
    const savedDarkMode = localStorage.getItem("darkMode");
    if (savedDarkMode === "true") {
      this.isDarkMode = true;
    }
    this.applyDarkMode();
    this.loadScript("https://checkout.razorpay.com/v1/checkout.js")
      .then(() => {
        this.scriptLoaded = true;
      })
      .catch((error) => {
        console.error("Error loading Razorpay script:", error);
      });
  },
  methods: {
    close() {
      this.$emit("close");
    },
    applyDarkMode() {
      const themeLinkElement = document.getElementById("theme-stylesheet");
      if (themeLinkElement) {
        themeLinkElement.href = this.themeLink;
      } else {
        console.error("Theme stylesheet link element not found");
      }
      document.body.classList.toggle("dark-mode", this.isDarkMode);
    },

    setAmount(amount) {
      this.manualAmount = amount;
    },
    async addMoney() {
  if (!this.isValidAmount(this.manualAmount)) {
    this.toast.error("Please enter a valid amount");
    return;
  }

  this.isLoading = true;

  try {
    const transaction = await this.createTransaction(this.manualAmount);
    const meta = await this.pollTransactionMeta(transaction.id);

    if (!meta || !meta.order_id) {
      throw new Error("Payment processing timed out");
    }

    if (!this.isRazorpayLoaded()) {
      throw new Error("Razorpay SDK failed to load. Check your network.");
    }

    this.isLoading = false;
    this.initiateRazorpayPayment(meta, transaction);
  } catch (error) {
    console.error("Error processing payment:", error);
    this.toast.error(error.message || "Error processing payment");
    this.isLoading = false;
  }
},
isValidAmount(amount) {
  return amount && amount > 0;
},

async createTransaction(amount) {
  const transaction = await TransactionService.createWalletTransaction(
    parseFloat(amount),
    "Top_Up"
  );
  if (!transaction) throw new Error("Failed to create transaction");
  return transaction;
},

async pollTransactionMeta(transactionId, maxAttempts = 10, interval = 1000) {
  let attempts = 0;
  while (attempts < maxAttempts) {
    const trans = await TransactionService.getTransaction(transactionId);
    const meta = trans.meta ? JSON.parse(trans.meta) : null;
    if (meta && meta.order_id) {
      return meta;
    }
    await this.delay(interval);
    attempts++;
  }
  return null;
},

isRazorpayLoaded() {
  return this.scriptLoaded && window.Razorpay;
},

initiateRazorpayPayment(meta, transaction) {
  const options = {
    key: meta.key_id,
    amount: meta.amount,
    currency: "INR",
    order_id: meta.order_id,
    handler: (response) => this.handlePaymentResponse(response, transaction),
    prefill: {
      name: meta.name,
      email: meta.email,
      contact: meta.contact,
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.open();
},

async handlePaymentResponse(response, transaction) {
  try {
    const verify = await fetch(
      "https://api.voltbrew.energy/api/wallet/verify-payment",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transactionId: transaction.id,
          ...response,
        }),
      }
    );

    const res = await verify.json();

    if (res.status === "success") {
      await this.updateWalletAfterSuccess(transaction);
    } else {
      await this.handlePaymentFailure(transaction);
    }
  } catch (err) {
    console.error("Error verifying payment:", err);
    this.toast.error("Error verifying payment");
  }
},

async updateWalletAfterSuccess(transaction) {
  this.walletBalance =
    Number(this.walletBalance) + Number(this.manualAmount);
  const allTransactionIds = [
    ...new Set([
      transaction.id,
      ...((await UserService.getWalletTransactions()) || []).map(
        (t) => t.id
      ),
    ]),
  ];

  this.wallet = await TransactionService.getWallet();
 const updatedWallet = await TransactionService.updateWallet(
    this.wallet[0].id,
    allTransactionIds
  );
  this.transactions=updatedWallet.transactions;
},

async handlePaymentFailure(transaction) {
  transaction.status = "Failed";
  this.transactions = [...this.transactions, transaction];
  this.toast.error("Payment failed");
},

delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
},

    async loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          this.scriptLoaded = true;
          return resolve(true);
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => {
          this.scriptLoaded = true;
          resolve(true);
        };
        script.onerror = () => reject(new Error("Razorpay SDK failed to load"));
        document.body.appendChild(script);
      });
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px 10px 10px;
  border-radius: 5px;
  width: 100%;
  height: 100vh;
  max-width: 600px;
}

.wallet-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.wallet-header h1 {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.back-button {
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.transaction-history {
  margin-top: 20px;
}

.transaction-history h2 {
  color: #453a94;
  text-align: left;
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

th,
td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  font-size: 12px;
}
.dark-mode td{
  color:#fff;
  font-weight:500;
}
th {
  background-color: #f2f2f2;
  color: #453a94;
}

.close-modal {
  margin-top: 20px;
  padding: 5px 20px;
  background-color: #453a94;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.performance {
  background: linear-gradient(to right, #050011, #120b41);
  color: #ffffff;
  position: relative;
  z-index: 123;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  margin-top: 20px;
  border-radius: 20px;
}

.wallet-info {
  display: flex;
  flex-direction: column;
}

.wallet-balance {
  font-size: 1.2rem;
  font-weight: bold;
  color: #ffffff;
  margin: 0;
}

.wallet-date {
  font-size: 0.9rem;
  color: #f1fb5c;
  margin: 5px 0 0;
  text-align: left;
}

.wallet-amount {
  font-size: 1.5rem;
  font-weight: bold;
  color: #ffffff;
  text-align: right;
}

.top-right-image {
  position: absolute;
  top: 0;
  right: 0px;
  width: 40%;
  height: auto;
  border-radius: 20px;
  z-index: -2;
}

@media screen and (width: 600px) and (height: 1024px) {
  .top-right-image {
    width: 30%;
  }
}

@media screen and (width: 820px) and (height: 1180px) {
  .top-right-image {
    width: 20%;
  }
}

@media screen and (width: 768px) and (height: 1024px) {
  .top-right-image {
    width: 20%;
  }
}

@media screen and (width: 1024px) and (height: 1366px) {
  .top-right-image {
    width: 15%;
  }
}

.add-money-section {
  margin-top: 20px;
  text-align: center;
}

.add-money-text {
  font-size: 1rem;
  font-weight: bold;
  color: #453a94;
  text-align: left;
  margin: 0;
}

.add-money-input {
  margin-top: 10px;
  padding: 10px;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.amount-pills {
  margin-top: 10px;
  display: flex;
  gap: 10px;
}

.amount-pill {
  padding: 2px 15px;
  color: #453a94;
  border: 1px solid #453a94;
  cursor: pointer;
  font-size: 12px;
  border-radius: 7px;
}

.add-money-button {
  margin-top: 20px;
  padding: 5px 20px;
  background-color: #453a94;
  color: white;
  border: none;
  border-radius: 5px;
  width: 100%;
  font-size: 14px;
}
.dark-mode .add-money-text {
  color: #00f2b2 !important;
}
.dark-mode .amount-pill {
  color: #00f2b2;
  border: 1px solid #00f2b2;
}
.dark-mode .add-money-button {
  background-color: #00f2b2;
  color: #000;
}
.dark-mode .close-modal {
  background-color: #00f2b2;
  color: #000;
}
.transaction-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.transaction-loader {
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  width:50px;
  height:50px;
  border-top: 4px solid #453a94;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.transaction-status {
  font-weight: bold;
}
.transaction-status.success {
  color: green;
}
.transaction-status.failed {
  color: red;
}
.transaction-status.pending {
  color: orange;
}
</style>